import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Bell, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * NotificationPreferences — /settings/notifications
 *
 * Reuses the existing notification-prefs backend rather than creating
 * a parallel system. Toggle groups are aligned with backend keys:
 *   outbid, ending_soon, auction_ended, auction_status_changed, marketing.
 */
const ITEMS = [
  { key: "outbid",                 label: "Outbid alerts",           desc: "You had the top bid and someone outbid you." },
  { key: "ending_soon",            label: "Ending-soon reminders",   desc: "Auctions on your watchlist close within the configured window." },
  { key: "auction_ended",          label: "Auction ended",           desc: "Any watched auction reaches its close time." },
  { key: "auction_status_changed", label: "Auction status changes",  desc: "Auction paused, cancelled, extended, or re-opened." },
  { key: "marketing",              label: "Marketing & promotions",  desc: "Occasional product updates and offers." },
];

export default function NotificationPreferences() {
  const nav = useNavigate();
  const [prefs, setPrefs] = useState(null);
  const [busy, setBusy] = useState(true);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const r = await api.get("/live-world/notif-prefs");
      setPrefs(r.data.prefs);
    } catch (e) {
      if (e?.response?.status === 401) { toast.error("Please log in."); nav("/"); return; }
      toast.error("Couldn't load preferences");
    } finally { setBusy(false); }
  }, [nav]);
  useEffect(() => { load(); }, [load]);

  const toggle = async (key) => {
    const next = { ...prefs, [key]: !prefs[key] };
    setPrefs(next);   // optimistic
    try {
      await api.patch("/live-world/notif-prefs", { [key]: next[key] });
    } catch {
      setPrefs((p) => ({ ...p, [key]: !next[key] }));
      toast.error("Couldn't save preference");
    }
  };

  if (busy || !prefs) {
    return <div className="min-h-[70vh] grid place-items-center text-stone-500"><Loader2 className="animate-spin"/></div>;
  }
  return (
    <div className="min-h-[calc(100vh-4rem)] bg-stone-50 pb-20" data-testid="notif-prefs">
      <div className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-stone-200">
        <div className="max-w-2xl mx-auto px-3 py-2 flex items-center gap-2">
          <button onClick={() => nav(-1)} className="p-2 -ml-1 rounded-full hover:bg-stone-100" data-testid="notif-prefs-back">
            <ArrowLeft size={18}/>
          </button>
          <Bell size={16} className="text-amber-600"/>
          <div className="font-black text-stone-900">Notifications</div>
        </div>
      </div>
      <div className="max-w-2xl mx-auto p-3 space-y-2">
        {ITEMS.map((it) => (
          <div key={it.key}
               className="bg-white border border-stone-200 rounded-2xl p-3 flex items-center gap-3"
               data-testid={`notif-row-${it.key}`}>
            <div className="flex-1 min-w-0">
              <div className="font-black text-stone-900 text-sm">{it.label}</div>
              <div className="text-[11px] text-stone-500 font-bold">{it.desc}</div>
            </div>
            <button onClick={() => toggle(it.key)}
                    role="switch"
                    aria-checked={!!prefs[it.key]}
                    className={`w-11 h-6 rounded-full transition-colors relative ${
                      prefs[it.key] ? "bg-emerald-500" : "bg-stone-300"
                    }`}
                    data-testid={`notif-toggle-${it.key}`}>
              <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${
                prefs[it.key] ? "translate-x-5" : "translate-x-0.5"
              }`}/>
            </button>
          </div>
        ))}
        <div className="text-[10px] text-stone-400 text-center italic pt-2">
          Preferences apply to Live World auctions and transaction updates.
          Ending-soon thresholds are set centrally by find2near.
        </div>
      </div>
    </div>
  );
}
